import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import jsQR from 'jsqr';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import {
    LoginResponse,
    LoginResponseType
} from './compiled_proto/com/celertech/baseserver/communication/login/DownstreamLoginProto';
import { fetchBuildRev } from './components/BuildRevision';
import { Button } from './components/common/Button';
import { ChangeLogsModal } from './components/common/ChangeLogsModal';
import ReadMore from './components/common/ReadMore';
import StatusIndicator from './components/common/StatusIndicator';
import { LoginFormValues, loginSchema } from './components/form/schema/loginSchema';
import RHFInput from './components/inputs/RHFInput';
import RefreshVersionModal from './components/modal/RefreshVersionModal';
import TwoFactorAuthModal from './components/modal/TwoFactorAuthModal';
import buildJson from './config/build.json';
import { APP_VERSION, changeLogs } from './config/version';
import {
    getPlatformLogo,
    isArivalPlatform,
    isGlobalBlockPlatform,
    isOexPlatform,
    isPagoFacilPlatform,
    isPaytechPlatform,
    isRoundedPlatform,
    isWolfBTCPlatform,
    isXellarPaymentsPlatform,
    logoTitle
} from './helpers/environmentHelper';
import { logToServer } from './services/LogService';
import { useAppDispatch, useAppSelector } from './state/hooks';
import { initializeAppVersion, selectAppVersion } from './state/reducers/appVersionSlice';
import {
    controlClearSubscriptions,
    doLogin,
    loginAttempt,
    LoginStatus,
    selectUserLoginAttempts
} from './state/reducers/authSlice';
import { selectSystemStatus } from './state/reducers/generalSlice';
import { selectTradingSettings, setTradingLayout } from './state/reducers/settingSlice';
import { useDisclosure } from './utils/hooks/useDisclosure';
import useLanguage from './utils/hooks/useLanguage';
import useLockoutCountdown from './utils/hooks/useLockoutCountdown';
import useToast from './utils/hooks/useToast';

interface ILoginFormValues extends LoginFormValues {
    server: unknown;
}

export interface LoginResponseWithSecret extends LoginResponse {
    secret?: string;
}

const defaultTradingLayout = window.config?.layout?.default;
const xplorspotEnabled = window.config?.layout?.xplorspot?.enabled;
const xplorspotliteEnabled = window.config?.layout?.xplorspotlite?.enabled;
const xplortraderEnabled = window.config?.layout?.xplortrader?.enabled;

const buildRev = buildJson.build;

function Login() {
    const lang = useLanguage();

    const dispatch = useAppDispatch();
    const systemStatus = useAppSelector(selectSystemStatus);
    const tradingSettings = useAppSelector(selectTradingSettings);
    const { currentVersion, shouldShowChangelog } = useAppSelector(selectAppVersion);

    const changeLogsDisclosure = useDisclosure(shouldShowChangelog);

    const navigate = useNavigate();
    const [toastError] = useToast();

    const refreshVersionDisclosure = useDisclosure(false);
    const twoFactorAuthDisclosure = useDisclosure(false);

    const latestRev = useSWR('buildRev', fetchBuildRev, {
        revalidateOnFocus: true,
        revalidateOnReconnect: true
    }).data;

    const [userRequires2FA, setUserRequires2FA] = useState<LoginResponse>();

    const form = useForm<ILoginFormValues>({
        defaultValues: {
            username: '',
            password: ''
        },
        mode: 'onChange',
        resolver: yupResolver(loginSchema(userRequires2FA))
    });
    const {
        watch,
        handleSubmit,
        setError,
        setValue,
        formState: { errors, isSubmitted, isSubmitting },
        register
    } = form;

    const onSubmit = async (data: ILoginFormValues) => {
        const { username, password, twoFactorToken } = data;
        try {
            const resp = (await dispatch(doLogin({ username, password, twoFactorToken })).unwrap()) as LoginResponse;

            if (resp.loginResponseType === LoginResponseType.QR_TOKEN_CODE_REQUIRED) {
                let response: LoginResponseWithSecret = resp;
                try {
                    const result = (await extractTotpSecretFromUint8(resp.qrCode)) as any;
                    response = { ...resp, secret: result?.secret };
                    console.log(response);
                } catch (e) {
                    console.log(e);
                }
                // Requires 2FA
                setUserRequires2FA(response);
                twoFactorAuthDisclosure[1].open();
            } else {
                // Assumes already logged in
                toast.dismiss();
                // Send user to celer screen

                // Force reload if build versions does not tally
                const tradingLayout = tradingSettings.tradingLayout?.value;

                if (tradingLayout === 'xplorspotlite' && xplorspotliteEnabled) navigate('/lite');
                else if (tradingLayout === 'xplortrader' && xplortraderEnabled) navigate('/trader');
                else if (tradingLayout === 'xplorspot' && xplorspotEnabled) navigate('/');
                else {
                    let path = '/';
                    if (defaultTradingLayout === 'xplorspotlite') path = '/lite';
                    if (defaultTradingLayout === 'xplortrader') path = '/trader';
                    dispatch(setTradingLayout(defaultTradingLayout));
                    navigate(path);
                }
            }
        } catch (e) {
            dispatch(loginAttempt({ username, success: false }));
            const err = e as { code: LoginStatus; message?: string };

            if (err.code === LoginStatus.BAD_CREDENTIALS) {
                if (twoFactorToken) {
                    // If twoFactorToken is provided in this authentication attempt, then
                    const msg = "We couldn't verify that code. Please try again.";
                    logToServer('info', `Couldn't verify user [${username}] 2fa code.`);
                    setError('server', { type: 'custom', message: msg });
                    toastError({ body: msg, title: 'Login Failure', persist: true });
                    cancelTwoFactorAuth();
                } else {
                    const msg = "We couldn't verify your credentials. Please try again.";
                    logToServer('info', `Couldn't verify user [${username}] credentials.`);
                    setError('server', { type: 'custom', message: msg });
                    toastError({ body: msg, title: 'Login Failure' });
                }
            } else if (err.code === LoginStatus.UNEXPECTED || systemStatus === 'down') {
                const msg =
                    "We're having trouble signing you in right now. Your account is safe — please try again shortly.";
                logToServer('info', `Trouble signing in user [${username}], unexpected error.`);
                setError('server', { type: 'custom', message: msg });
                toastError({ body: msg, title: 'Login Failure' });
            }
        }
    };

    const cancelTwoFactorAuth = () => {
        twoFactorAuthDisclosure[1].close();
        setValue('twoFactorToken', undefined);
        setTimeout(() => setUserRequires2FA(undefined), 300);
    };

    useEffect(() => {
        if (latestRev && buildRev !== latestRev) refreshVersionDisclosure[1].open();
    }, [latestRev]);

    useEffect(() => {
        if (shouldShowChangelog) changeLogsDisclosure[1].open();
    }, [shouldShowChangelog]);

    const userLoginAttempts = useAppSelector(selectUserLoginAttempts);
    const username = watch('username');

    const userLockedOut = useMemo(() => {
        const userState = userLoginAttempts[username];
        if (userState?.lockoutUntil && Date.now() < userState.lockoutUntil) return true;
        else return false;
    }, [userLoginAttempts, username]);

    const lockoutTime = useLockoutCountdown(username);

    useEffect(() => {
        dispatch(controlClearSubscriptions());
        dispatch(initializeAppVersion(APP_VERSION));
    }, []);

    const showChangeLogModal = useMemo(() => {
        return changeLogs.length > 0 && currentVersion === APP_VERSION;
    }, [changeLogs, currentVersion]);

    return (
        <div className={cn('h-full flex flex-col justify-center py-12 px-6 bg-brand-background')}>
            <LoginLogo />
            {/* <div className="absolute top-6 right-6 flex items-center gap-4">
                <ThemeButton className="w-4 h-4" />
                <LanguageButton className="w-4 h-4" />
            </div> */}
            <div className="absolute bottom-6 right-6 flex items-center gap-4">
                <div
                    className="relative text-xs font-medium text-brand-neutral-300 hover:text-brand-text cursor-pointer"
                    onClick={() => changeLogsDisclosure[1].open()}>
                    v{currentVersion} <span className="text-brand-neutral-400 text-xs">({buildJson.build})</span>
                </div>
            </div>
            <FormProvider {...form}>
                <form className="relative" onSubmit={handleSubmit(onSubmit)}>
                    <div className="relative my-6 mx-auto w-full max-w-md">
                        <div
                            className={cn('p-4 sm:p-6 rounded-md sm:rounded-lg sm:px-8 brand-shadow z-0', {
                                'bg-brand-background': !isPagoFacilPlatform,
                                'bg-brand-background-dark': isPagoFacilPlatform
                            })}>
                            <div className="absolute top-4 right-4">
                                <StatusIndicator showLabel={false} size="md" />
                            </div>
                            <div className="flex flex-col gap-5">
                                <div className="flex gap-1 items-center justify-center text-center text-sm mb-4">
                                    <p className="text-brand-text">{lang.login.title}</p>
                                </div>
                                <RHFInput
                                    {...register('username')}
                                    type="username"
                                    label={lang.login.username}
                                    autoComplete="username"
                                />
                                <RHFInput
                                    {...register('password')}
                                    type="password"
                                    label={lang.login.password}
                                    autoComplete="current-password"
                                />
                                {errors['server'] && isSubmitted && !userLockedOut ? (
                                    <span className="text-brand-red text-xs">{errors['server'].message}</span>
                                ) : null}
                                {userLockedOut && (
                                    <span className="text-brand-red text-xs">
                                        Too many failed attempts. Please wait {lockoutTime} before logging in again.
                                    </span>
                                )}
                                <Button isLoading={isSubmitting} disabled={userLockedOut}>
                                    {lang.login.loginButton}
                                </Button>
                            </div>
                            <div className="flex flex-col items-center mt-6 gap-3">
                                <div
                                    className="relative text-sm font-medium text-brand-neutral-300 hover:text-brand-text cursor-pointer"
                                    onClick={() => navigate('/forgot-password')}>
                                    {lang.login.forgotPassword}
                                </div>
                            </div>
                        </div>
                    </div>
                    {!isWolfBTCPlatform && <LoginFooter />}
                    <RefreshVersionModal opened={refreshVersionDisclosure[0]} handlers={refreshVersionDisclosure[1]} />
                    <TwoFactorAuthModal
                        opened={twoFactorAuthDisclosure[0]}
                        handlers={twoFactorAuthDisclosure[1]}
                        userRequires2FA={userRequires2FA}
                        manualOnSubmit={handleSubmit(onSubmit)}
                        cancelTwoFactorAuth={cancelTwoFactorAuth}
                    />

                    {showChangeLogModal && (
                        <ChangeLogsModal
                            version={currentVersion}
                            opened={changeLogsDisclosure[0]}
                            handlers={changeLogsDisclosure[1]}
                        />
                    )}
                </form>
            </FormProvider>
        </div>
    );
}

export default Login;

export const LoginLogo = () => {
    return (
        <div className="mx-auto w-full max-w-md">
            {!isRoundedPlatform &&
                !(
                    isPaytechPlatform ||
                    isWolfBTCPlatform ||
                    isGlobalBlockPlatform ||
                    isXellarPaymentsPlatform ||
                    isPagoFacilPlatform ||
                    isOexPlatform
                ) && <img className="mx-auto h-8 w-auto" src={getPlatformLogo()} alt={logoTitle} />}
            {isRoundedPlatform && (
                <div
                    className={cn('flex flex-col h-16 relative mx-auto w-fit', {
                        'pr-4': isArivalPlatform
                    })}>
                    {isArivalPlatform && <img className="block max-h-full" src={getPlatformLogo()} alt={logoTitle} />}
                    {isArivalPlatform && (
                        <div className="text-sm absolute -bottom-0.5 left-16 text-brand-primary-darker whitespace-nowrap">
                            Powered by GCEX
                        </div>
                    )}
                </div>
            )}
            {(isPaytechPlatform ||
                isWolfBTCPlatform ||
                isGlobalBlockPlatform ||
                isXellarPaymentsPlatform ||
                isPagoFacilPlatform ||
                isOexPlatform) && (
                <div className={cn('flex flex-col h-16 relative mx-auto w-fit justify-center items-center')}>
                    <img className="block max-h-full" src={getPlatformLogo()} alt={logoTitle} />
                </div>
            )}
        </div>
    );
};

export const LoginFooter = () => {
    const lang = useLanguage();
    return (
        <Fragment>
            <div className="absolute -bottom-6 left-0 right-0 mx-auto flex gap-2 text-xs text-center justify-center items-center text-brand-neutral-400 flex-wrap cursor-default z-10">
                <div
                    className="cursor-pointer hover:text-brand-primary-light"
                    onClick={() => {
                        let termsOfBusiness = 'https://knowledge.gc.exchange/knowledge/what-are-gcex-terms-of-business';

                        if (isXellarPaymentsPlatform) termsOfBusiness = 'https://docs.ex.uptex.com/faq';
                        window.open(termsOfBusiness, '_blank');
                    }}>
                    {lang.login.termsOfBusiness}
                </div>
                |
                <div
                    className="cursor-pointer hover:text-brand-primary-light"
                    onClick={() => {
                        let contactUs =
                            'https://knowledge.gc.exchange/knowledge/what-are-your-emergency-contacts-details';

                        if (isXellarPaymentsPlatform) contactUs = 'https://odoo.uptex.com/helpdesk';
                        window.open(contactUs, '_blank');
                    }}>
                    {lang.login.contactUs}
                </div>
                |
                <div
                    className="cursor-pointer hover:text-brand-primary-light"
                    onClick={() => {
                        let helpAndResources = 'https://knowledge.gc.exchange/knowledge';

                        if (isXellarPaymentsPlatform) helpAndResources = 'https://docs.ex.uptex.com/guide/';
                        window.open(helpAndResources, '_blank');
                    }}>
                    {lang.login.helpAndResources}
                </div>
            </div>
            {isPaytechPlatform && (
                <div className="flex flex-col gap-2 text-xs text-center text-brand-neutral-400 max-w-xl mx-auto">
                    <div>Powered by GCEX</div>
                    <div>
                        All trading services are provided exclusively by GC Exchange FZE, which is authorised and
                        regulated by Dubai{"'"}s Virtual Assets Regulatory Authority. PayTech LLC FZ is a technology
                        platform only, does not provide financial services, and acts solely as a brand agent of GC
                        Exchange FZE
                    </div>
                </div>
            )}
            {isXellarPaymentsPlatform && (
                <div className="flex flex-col gap-2 text-xs text-center text-brand-neutral-400 max-w-xl mx-auto">
                    <div>Powered by GCEX</div>
                    <ReadMore
                        text={`All crypto-asset services are provided exclusively by the relevant GCEX entity, being either GC
                        Exchange A/S, which is authorised and regulated by the Danish Financial Supervisory Authority
                        (Finanstilsynet) as a Crypto-Asset Service Provider under the EU Markets in Crypto-Assets
                        Regulation (MiCA) and as a Currency Exchange (FTID 45020), or GC Exchange FZE, which is
                        authorised and regulated by the Dubai Virtual Assets Regulatory Authority (VARA), depending on
                        the jurisdiction in which the client is onboarded. Xellar Payments is a technology and branding
                        platform only. It does not provide crypto-asset services or any other regulated financial
                        services and acts solely as a branded interface for the relevant GCEX entity.`}
                    />
                </div>
            )}
            {isPagoFacilPlatform && (
                <div className="flex flex-col gap-2 text-xs text-center text-brand-neutral-400 max-w-xl mx-auto">
                    <div>Powered by GCEX</div>
                    <ReadMore
                        text={`All crypto asset services are provided exclusively by GC Exchange FZE and GC Exchange A/S, which are authorised and regulated by Dubai's Virtual Assets Regulatory Authority (VARA) and the Danish Financial Supervisory Authority (Finanstilsynet/Danish FSA) under MiCA. Stratford Solutions is a technology platform only, does not provide financial services, and acts solely as a brand agent of GC Exchange FZE GC Exchange A/S.`}
                    />
                </div>
            )}
            {isOexPlatform && (
                <div className="flex flex-col gap-2 text-xs text-center text-brand-neutral-400 max-w-xl mx-auto">
                    <div>Powered by GCEX</div>
                    <ReadMore
                        text={`All crypto asset services are provided exclusively by GC Exchange FZE and GC Exchange A/S, which are authorised and regulated by Dubai's Virtual Assets Regulatory Authority (VARA) and the Danish Financial Supervisory Authority (Finanstilsynet/Danish FSA) under MiCA. OEX is a technology platform only, does not provide financial services, and acts solely as a brand agent of GC Exchange FZE GC Exchange A/S.`}
                    />
                </div>
            )}
        </Fragment>
    );
};

export async function extractTotpSecretFromUint8(uint8Input) {
    // normalize input (object -> Uint8Array)
    const uint8 = uint8Input instanceof Uint8Array ? uint8Input : new Uint8Array(Object.values(uint8Input));

    // convert to image
    const blob = new Blob([uint8], { type: 'image/png' });
    const url = URL.createObjectURL(blob);

    const img = new Image();

    return new Promise((resolve, reject) => {
        img.onload = () => {
            try {
                // draw to canvas
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d') as any;

                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

                // decode QR
                const code = jsQR(imageData.data, imageData.width, imageData.height);

                if (!code) {
                    return reject(new Error('QR code not detected'));
                }

                const data = code.data;

                // extract TOTP secret
                const match = data.match(/secret=([A-Z2-7]+)/i);

                if (!match) {
                    return reject(new Error('No TOTP secret found in QR payload'));
                }

                resolve({
                    raw: data,
                    secret: match[1]
                });
            } catch (err) {
                reject(err);
            } finally {
                URL.revokeObjectURL(url);
            }
        };

        img.onerror = () => reject(new Error('Failed to load image from Uint8Array'));

        img.src = url;
    });
}
